import   React                      ,
       { useState                  ,
         useMemo                   ,
         useEffect                 ,
         useRef                    }  from 'react';

import   classNames                    from 'classnames';

import   styles                        from './AdminSheet.module.scss';

import   CountrySelector                from '../../widget/inputs/CountrySelector';
import   Field                         from '../../widget/inputs/Field';
import   FieldMedia                    from '../../widget/inputs/FieldMedia';
import   Select                        from '../../widget/inputs/Select';
import   TextArea                      from '../../widget/inputs/TextArea';
import   Toggle                        from '../../widget/inputs/Toggle';
import   SwitchOnOff                   from '../../widget/inputs/SwitchOnOff';
import   Trigger                       from '../../widget/actuators/Trigger';
import   WidgetFrame                   from '../../widget/navigation/WidgetFrame';
import   Window                        from '../../widget/windows/window';
import   AnimatedWaitingIcon2          from '../../widget/animations/animatedWaitingIcon2';
import   AdminResourcesTab               from './AdminResourcesTab';
import   AdminEventsTab                  from './AdminEventsTab';
import   Tabs2                         from '../../widget/navigation/Tabs2';

import Icon           from '../../widget/images/Icon';

import   PurchaseManagment              from '../purchaseManagment/PurchaseManagment';
import   SubscriptionManagment          from '../subscriptionManagment/SubscriptionManagment';
import   SentEmailManagment             from '../sentEmailManagment/SentEmailManagment';
import   AdminActionLogManagment        from '../adminActionLogManagment/AdminActionLogManagment';


import { Admin                      ,
         ACCOUNT_STATUS_VALUES      ,
         ACCOUNT_STATUS_LABELS      }  from '../../../dataTypes/adminManagment/types';
import { DUREE_SANS_LIMITE_SECONDES ,
         estDureeSansLimite         ,
         SANS_LIMITE                ,
         estSansLimite              }  from '../../../dataTypes/adminManagment/quota';
import { getInitials                ,
         generateInitialsAvatarDataUrl ,
         formatSubscriberCount         ,
         formatDateOrDash              }  from '../../../dataTypes/adminManagment/utils';
import { timeStringToSeconds }  from '../../../dataTypes/timeManagment/utils';
import { createAdmin                ,
         updateAdmin                ,
         updateAdminPhoto           ,
         updateAdminBanner          ,
         getAdminPhotoUrl           ,
         checkSlugExistenceAdmin    ,
         resetAdminUsageCounters    ,
         AdminUsageCounterField     }  from '../../../backendServices/adminManagment/api';
import { PROMOTION_CONFIRMATION_REQUIRED_CODE }  from '../../../dataTypes/backendServicesManagment/types';
import { generateSlug               }  from '../../../utils/utils_string';
import { useTypedSelector           ,
         useTypedDispatch           }  from '../../../store/useTypeSelector';
import { RootState                  }  from '../../../store/generalReducer';
import { refreshAdmin               }  from '../../../store/adminAuthenticationManagment/actions';
import { listAdminSocialNetworks    ,
         getSocialNetworkAuthorizeUrl ,
         disconnectSocialNetwork     ,
         linkInstagramViaFacebook    ,
         AdminSocialNetworkStatus    }  from '../../../backendServices/adminManagment/api_socialNetworks';

// ── Icône "Enregistrer" — même SVG que BroadcastingChannelConfig.tsx (Ico.Save) ──
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

// HH:MM:SS sans limite à 24h (contrairement à un formatage basé sur Date) — un
// quota de durée cumulée peut dépasser un jour.
const formatHms = (totalSeconds: number): string => {
  const s  = Math.max(0, Math.round(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
};

// Badge cliquable remplaçant le champ de saisie du plafond quand un quota (durée,
// appareils, chaînes, disque) est en mode illimité : gris en mode limité, vert en
// mode illimité. Un clic bascule l'état, comme le faisait l'ancien switch.
const UnlimitedBadge = ({ active, onClick }: { active: boolean; onClick: () => void }) => (
  <span
    className={classNames(styles.unlimitedBadge, active && styles.unlimitedBadge_active)}
    onClick={onClick}
  >
    Illimité
  </span>
);

// HH:MM — heure de la journée (remise à zéro des quotas de durée), distinct de
// formatHms qui formate une DURÉE (HH peut dépasser 24, il y a des secondes).
const formatHHMM = (secondsSinceMidnight: number): string => {
  const s  = ((Math.round(secondsSinceMidnight) % 86400) + 86400) % 86400;
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}`;
};

const RESET_FREQUENCY_VALUES = [ 'monthly'     , 'weekly'       , 'daily'        ];
const RESET_FREQUENCY_LABELS = [ 'Mensuelle'   , 'Hebdomadaire' , 'Journalière'  ];

// ISO 8601 : 1 = lundi .. 7 = dimanche.
const WEEKDAY_VALUES = [ 1      , 2      , 3          , 4      , 5         , 6       , 7          ];
const WEEKDAY_LABELS = [ 'Lundi', 'Mardi', 'Mercredi' , 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche' ];

const MONTH_DAY_VALUES = Array.from({ length: 31 }, (_, i) => i + 1); // 1..31

// Deuxième ligne des quotas "Durée diffusion" / "Durée enregistrement", visible
// uniquement en mode limité : fréquence de remise à zéro du compteur consommé, et
// jour/heure de prise en compte — mensuelle (jour du mois), hebdomadaire (jour de
// la semaine) ou journalière (heure seule).
interface ResetScheduleRowProps {
  frequency : 'daily' | 'weekly' | 'monthly';
  day       : number;
  time      : number; // secondes depuis minuit
  onFrequencyChange : (value: string) => void;
  onDayChange       : (value: string) => void;
  onTimeChange      : (value: string) => void;
}

const ResetScheduleRow = ({ frequency, day, time, onFrequencyChange, onDayChange, onTimeChange }: ResetScheduleRowProps) => (
  <div className={classNames(styles.resetScheduleRow)}>
    <span className={classNames(styles.text)}>Remise à zéro :</span>
    <Select choices          = {RESET_FREQUENCY_VALUES}
            choicesLabel     = {RESET_FREQUENCY_LABELS}
            value            = {frequency}
            showPlaceholder  = {false}
            width            = "130px"
            onChange         = {(e) => onFrequencyChange(e.target.value)} />
    {frequency === 'monthly' &&
      <Select choices          = {MONTH_DAY_VALUES.map(String)}
              choicesLabel     = {MONTH_DAY_VALUES.map((d) => `Le ${d}`)}
              value            = {String(day)}
              showPlaceholder  = {false}
              width            = "90px"
              onChange         = {(e) => onDayChange(e.target.value)} />}
    {frequency === 'weekly' &&
      <Select choices          = {WEEKDAY_VALUES.map(String)}
              choicesLabel     = {WEEKDAY_LABELS}
              value            = {String(day)}
              showPlaceholder  = {false}
              width            = "120px"
              onChange         = {(e) => onDayChange(e.target.value)} />}
    <span className={classNames(styles.text)}>à</span>
    <Field type="text" width="70px" placeholder="HH:MM" value={formatHHMM(time)}
           onChange={(e, value) => onTimeChange(String(value))} />
  </div>
);

const QUOTA_DISK_UNIT_CHOICES  = [ 'octets' , 'Ko' , 'Mo' , 'Go' ];
const QUOTA_DISK_UNIT_DIVISORS : Record<string, number> = { octets: 1, Ko: 1024, Mo: 1024 ** 2, Go: 1024 ** 3 };

/**
 * Unité d'affichage de départ pour un quota déjà enregistré.
 *
 * Le mégaoctet est le défaut, et le PLAFOND : un quota d'une centaine de Go
 * s'affiche donc en Mo plutôt qu'en Go. C'est l'unité dans laquelle ces quotas
 * se saisissent, et changer d'échelle d'un compte à l'autre rendrait deux fiches
 * incomparables au premier coup d'œil. Le sélecteur reste libre — « Go » est
 * toujours à un clic.
 *
 * On ne descend que sous le seuil du mégaoctet, et pour une seule raison : avec
 * deux décimales, 500 octets s'affichait « 0.00 Mo », et toucher au champ
 * remettait le quota à zéro.
 */
const pickQuotaDiskUnit = (bytes: number): string => {
  if (bytes >= QUOTA_DISK_UNIT_DIVISORS.Mo) return 'Mo';
  if (bytes >= QUOTA_DISK_UNIT_DIVISORS.Ko) return 'Ko';
  // Un compte neuf (0) part en Mo comme les autres : il n'y a rien à lire, et
  // c'est bien dans cette unité que le quota va être saisi.
  return bytes > 0 ? 'octets' : 'Mo';
};

export interface AdminSheetProps {
  admin?    : Admin;
  onCreate? : (id: number) => void;
  onEdit?   : (id: number) => void;
}

export const AdminSheet = ({ admin    ,
                             onCreate ,
                             onEdit   }: AdminSheetProps) => {

  const isEditMode = admin !== undefined;

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const dispatch           = useTypedDispatch();

  // ── Réseaux sociaux ── état et appels indépendants de profilePayload/
  // updateAdmin, comme l'annuaire Xtream : sa propre liste, ses propres
  // actions, jamais mêlé au gros PATCH de la fiche (une connexion vaut pour
  // elle-même, pas pour un brouillon qu'on pourrait annuler).
  const [ socialNetworks , setSocialNetworks ] = useState<AdminSocialNetworkStatus[]>([]);

  const reloadSocialNetworks = () => {
    if (!isEditMode || !admin) { return; }

    listAdminSocialNetworks(admin.id)
      .then(setSocialNetworks)
      .catch(() => { /* la section reste vide plutôt que de bloquer le reste de la fiche */ });
  };

  useEffect(() => {
    reloadSocialNetworks();

    // La connexion Google s'ouvre dans un nouvel onglet (SocialNetworkOAuth
    // ne peut pas rediriger cette fenêtre-ci sans la faire quitter la fiche) :
    // on rafraîchit au retour de focus, le moment où l'onglet de consentement
    // a le plus de chances de s'être refermé.
    window.addEventListener('focus', reloadSocialNetworks);
    return () => window.removeEventListener('focus', reloadSocialNetworks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.id]);

  const handleConnectSocialNetwork = (code: string) => {
    if (!admin) { return; }

    getSocialNetworkAuthorizeUrl(admin.id, code)
      .then(url => { window.open(url, '_blank', 'noopener,noreferrer'); })
      .catch(() => { /* pas de fenêtre d'erreur dédiée : un nouvel essai suffit */ });
  };

  const handleDisconnectSocialNetwork = (adminSocialNetworkId: number) => {
    if (!admin) { return; }

    disconnectSocialNetwork(admin.id, adminSocialNetworkId)
      .then(reloadSocialNetworks)
      .catch(() => { /* idem */ });
  };

  // Deuxième chemin de connexion Instagram (cf. api_socialNetworks.ts) : pas
  // de fenêtre de consentement, juste un appel serveur qui réutilise la Page
  // Facebook déjà connectée — d'où pas de window.open ici, contrairement à
  // handleConnectSocialNetwork.
  const handleLinkInstagramViaFacebook = () => {
    if (!admin) { return; }

    linkInstagramViaFacebook(admin.id)
      .then(reloadSocialNetworks)
      .catch(() => { /* pas de fenêtre d'erreur dédiée : un nouvel essai suffit */ });
  };

  // ── Compte ──
  const [ email             , setEmail             ] = useState(admin?.email ?? '');
  const [ password          , setPassword          ] = useState('');

  // ── Fenêtre de saisie du mot de passe ──
  const [ showPasswordWindow , setShowPasswordWindow ] = useState(false);
  const [ passwordDraft1     , setPasswordDraft1     ] = useState('');
  const [ passwordDraft2     , setPasswordDraft2     ] = useState('');
  const [ passwordDraftError , setPasswordDraftError ] = useState('');

  // ── Identité (colonne 2) ──
  const [ pseudonym         , setPseudonym         ] = useState(admin?.pseudonym       ?? '');
  const [ firstname         , setFirstname         ] = useState(admin?.firstname       ?? '');
  const [ lastname          , setLastname          ] = useState(admin?.lastname        ?? '');
  const [ profileRole       ] = useState(admin?.profileRole     ?? 'utilisateur');
  const [ biography         , setBiography         ] = useState(admin?.biography       ?? '');
  const [ personalPageUrl   , setPersonalPageUrl   ] = useState(admin?.personalPageUrl ?? '');

  // ── Reporter Kleon : accepte d'être sollicité pour couvrir un événement ──
  const [ reporterAvailable , setReporterAvailable ] = useState(admin?.reporterAvailable ?? false);
  const [ reporterCity      , setReporterCity      ] = useState(admin?.reporterCity ?? '');
  const [ reporterRadiusKm  , setReporterRadiusKm  ] = useState(admin?.reporterRadiusKm != null ? String(admin.reporterRadiusKm) : '');

  // ── Statut (colonne 3) — nouveau compte : non vérifié + en attente (forcé aussi côté backend) ──
  const [ verified          , setVerified          ] = useState(admin?.verified      ?? false);
  const [ accountStatus     , setAccountStatus     ] = useState(admin?.accountStatus ?? 'pending');
  const [ useFiligrane      , setUseFiligrane      ] = useState(admin?.useFiligrane  ?? false);
  // Quotas de durée cumulée (diffusion / enregistrement) — stockés en secondes,
  // saisis en minutes (Field convertit dans les handlers ci-dessous).
  const [ durationMaxBroadcasting , setDurationMaxBroadcasting ] = useState(admin?.durationMaxBroadcasting ?? 3600);
  const [ durationMaxRecording    , setDurationMaxRecording    ] = useState(admin?.durationMaxRecording    ?? 3600);

  // Dernière valeur "limitée" connue de chaque quota de durée, pour la restaurer
  // en repassant du mode illimité au mode limité (sinon on retomberait sur un
  // 1h par défaut arbitraire à chaque bascule).
  const durationMaxBroadcastingAvantIllimite = useRef(
    !estDureeSansLimite(admin?.durationMaxBroadcasting ?? 3600) ? (admin?.durationMaxBroadcasting ?? 3600) : 3600
  );
  const durationMaxRecordingAvantIllimite = useRef(
    !estDureeSansLimite(admin?.durationMaxRecording ?? 3600) ? (admin?.durationMaxRecording ?? 3600) : 3600
  );

  // Remise à zéro périodique des quotas de durée (visible seulement en mode limité, cf. rendu plus bas).
  const [ durationMaxBroadcastingResetFrequency , setDurationMaxBroadcastingResetFrequency ] = useState(admin?.durationMaxBroadcastingResetFrequency ?? 'monthly');
  const [ durationMaxBroadcastingResetDay       , setDurationMaxBroadcastingResetDay       ] = useState(admin?.durationMaxBroadcastingResetDay       ?? 1);
  const [ durationMaxBroadcastingResetTime      , setDurationMaxBroadcastingResetTime      ] = useState(admin?.durationMaxBroadcastingResetTime      ?? 0);
  const [ durationMaxRecordingResetFrequency    , setDurationMaxRecordingResetFrequency    ] = useState(admin?.durationMaxRecordingResetFrequency    ?? 'monthly');
  const [ durationMaxRecordingResetDay          , setDurationMaxRecordingResetDay          ] = useState(admin?.durationMaxRecordingResetDay          ?? 1);
  const [ durationMaxRecordingResetTime         , setDurationMaxRecordingResetTime         ] = useState(admin?.durationMaxRecordingResetTime         ?? 0);

  // À la création, le formulaire affiche un jour/heure de reset figé (1, minuit) sans
  // rapport avec l'instant réel de création du compte — contrairement à AdminCreation, qui
  // calcule cette date à partir de l'instant réel si l'appelant ne la précise pas. On ne
  // veut donc envoyer ces 3 champs (par groupe diffusion/enregistrement) que si l'opérateur
  // y a explicitement touché ; sinon, on laisse ce calcul backend s'appliquer.
  const durationMaxBroadcastingResetTouched = useRef(false);
  const durationMaxRecordingResetTouched    = useRef(false);

  // ── Quotas (colonne 3) ──
  // Defaults posés par AdminCreation à la création (2 devices/1 chaîne/2 Go) : préremplis
  // ici pour qu'un nouveau compte affiche d'emblée la vraie valeur qui sera enregistrée
  // (et reste éditable avant création), au lieu d'un 0/0/0 arbitraire du formulaire vierge.
  const QUOTA_DISK_DEFAUT     = 2 * 1024 ** 3;
  const NB_DEVICES_MAX_DEFAUT = 2;
  const NB_CHANNELS_MAX_DEFAUT = 1;

  const [ quotaDisk         , setQuotaDisk         ] = useState(admin?.quotaDisk       ?? QUOTA_DISK_DEFAUT);
  // Le quota est stocké en octets, mais personne ne le saisit dans cette unité :
  // l'affichage et la saisie partent du mégaoctet, sauf pour un quota trop petit
  // pour s'y lire. Changer d'unité ne touche qu'à la présentation, la valeur
  // envoyée reste en octets.
  const [ quotaDiskUnit     , setQuotaDiskUnit     ] = useState(() => pickQuotaDiskUnit(admin?.quotaDisk ?? QUOTA_DISK_DEFAUT));
  const [ nbDevicesMax      , setNbDevicesMax      ] = useState(admin?.nbDevicesMax    ?? NB_DEVICES_MAX_DEFAUT);
  const [ nbChannelsMax     , setNbChannelsMax     ] = useState(admin?.nbChannelsMax   ?? NB_CHANNELS_MAX_DEFAUT);

  // Dernière valeur "limitée" connue de chaque quota, pour la restaurer en repassant
  // du mode illimité au mode limité — même mécanisme que les quotas de durée ci-dessus.
  const quotaDiskAvantIllimite = useRef(
    !estSansLimite(admin?.quotaDisk ?? QUOTA_DISK_DEFAUT) ? (admin?.quotaDisk ?? QUOTA_DISK_DEFAUT) : QUOTA_DISK_DEFAUT
  );
  const nbDevicesMaxAvantIllimite = useRef(
    !estSansLimite(admin?.nbDevicesMax ?? NB_DEVICES_MAX_DEFAUT) ? (admin?.nbDevicesMax ?? NB_DEVICES_MAX_DEFAUT) : NB_DEVICES_MAX_DEFAUT
  );
  const nbChannelsMaxAvantIllimite = useRef(
    !estSansLimite(admin?.nbChannelsMax ?? NB_CHANNELS_MAX_DEFAUT) ? (admin?.nbChannelsMax ?? NB_CHANNELS_MAX_DEFAUT) : NB_CHANNELS_MAX_DEFAUT
  );

  // ── Localisation (colonne 3) ──
  const [ address           , setAddress           ] = useState(admin?.address ?? '');
  const [ zipCode           , setZipCode           ] = useState(admin?.zipCode ?? '');
  const [ city              , setCity              ] = useState(admin?.city    ?? '');
  const [ region            , setRegion            ] = useState(admin?.region  ?? '');
  const [ country           , setCountry           ] = useState(admin?.country ?? '');

  const [ emailErrorMessage     , setEmailErrorMessage     ] = useState('');
  const [   , setPasswordErrorMessage  ] = useState('');
  const [ firstnameErrorMessage , setFirstnameErrorMessage ] = useState('');
  const [ lastnameErrorMessage  , setLastnameErrorMessage  ] = useState('');
  const [ pseudonymErrorMessage , setPseudonymErrorMessage ] = useState('');

  // Slug dérivé du pseudonyme, conservé en BDD et affiché en liste sous la forme "@slug"
  const slug = useMemo(() => generateSlug(pseudonym), [pseudonym]);

  // Vérification en direct : le slug dérivé du pseudo saisi est-il déjà pris par un autre compte ?
  useEffect(() => {

    if (slug === '') { setPseudonymErrorMessage(''); return; }

    // Le pseudo n'a pas changé depuis le chargement : son propre slug ne peut pas être en conflit
    if (isEditMode && admin && slug === admin.slug) { setPseudonymErrorMessage(''); return; }

    let cancelled = false;

    checkSlugExistenceAdmin(slug, isEditMode && admin ? admin.id : undefined).then((result) => {
      if (cancelled) return;
      setPseudonymErrorMessage(result.code === 1 && result.payload ? 'Ce pseudo existe déjà' : '');
    });

    return () => { cancelled = true; };

  }, [slug, isEditMode, admin]);

  const [ saveButtonLock    , setSaveButtonLock    ] = useState(false);
  const [ saved             , setSaved             ] = useState(false);
  const [ error             , setError             ] = useState(false);
  const [ errorMsg          , setErrorMsg          ] = useState('');

  // Un compte client existe déjà avec cet e-mail : AdminCreation attend une confirmation
  // explicite (isPromotion: true) avant de le transformer en créateur.
  const [ showPromotionConfirmation , setShowPromotionConfirmation ] = useState(false);

  // Aucun mot de passe saisi à la création : on ne l'impose pas (une promotion n'en a pas
  // besoin, cf. profilePayload) mais on prévient l'opérateur qu'un mot de passe sera généré
  // automatiquement côté serveur (AdminCreation/PasswordGenerator), avant d'envoyer quoi
  // que ce soit.
  const [ showNoPasswordConfirmation , setShowNoPasswordConfirmation ] = useState(false);

  // ── Réinitialisation des compteurs d'usage (appareils, durée diffusion, durée
  // enregistrement) — action séparée du gros PATCH de la fiche, pour la même raison
  // qu'AdminUpdate ne touche jamais ces compteurs côté backend (cf. AdminUsageReset.php) :
  // ce n'est pas une édition de profil, c'est un rattrapage manuel. Un petit bouton par
  // compteur, donc l'état retient QUEL champ est en cours de confirmation/traitement.
  const [ usageResetPendingField , setUsageResetPendingField ] = useState<AdminUsageCounterField | null>(null);
  const [ usageResetLockedFields , setUsageResetLockedFields ] = useState<Partial<Record<AdminUsageCounterField, boolean>>>({});
  const [ usageResetResult       , setUsageResetResult       ] = useState<null | { error: boolean; message: string }>(null);
  // Icône d'attente tant que la remise à zéro est en cours (peut être longue côté serveur).
  const [ usageResetInProgress   , setUsageResetInProgress   ] = useState(false);

  const USAGE_RESET_LABELS: Record<AdminUsageCounterField, string> = {
    nbDevicesUsed               : "le nombre d'appareils"    ,
    durationElapsedBroadcasting : "la durée de diffusion"    ,
    durationElapsedRecording    : "la durée d'enregistrement",
  };

  // ── Photo de profil (colonne 1) ──
  const [ photoUpload            , setPhotoUpload            ] = useState<File | null>(null);
  const [ photoMarkedForDeleting , setPhotoMarkedForDeleting ] = useState(false);
  const [ photoErrorMessage      , setPhotoErrorMessage      ] = useState('');
  const [ photoResetKey          , setPhotoResetKey          ] = useState(0);

  const defaultPhotoImage = useMemo(() => {
    const initials = getInitials(firstname, lastname, pseudonym);
    return generateInitialsAvatarDataUrl(initials) || '/images/logo2.png';
  }, [firstname, lastname, pseudonym]);

  // ── Image de bandeau (colonne 1) ──
  const [ bannerUpload            , setBannerUpload            ] = useState<File | null>(null);
  const [ bannerMarkedForDeleting , setBannerMarkedForDeleting ] = useState(false);
  const [       , setBannerErrorMessage      ] = useState('');

  const handleEmailChange       = (e: React.ChangeEvent<HTMLInputElement >) => { setEmail           (e.target.value); setEmailErrorMessage     (''); };
  const handlePseudonymChange   = (e: React.ChangeEvent<HTMLInputElement >) => { setPseudonym       (e.target.value); };
  const handleFirstnameChange   = (e: React.ChangeEvent<HTMLInputElement >) => { setFirstname       (e.target.value); setFirstnameErrorMessage (''); };
  const handleLastnameChange    = (e: React.ChangeEvent<HTMLInputElement >) => { setLastname        (e.target.value); setLastnameErrorMessage  (''); };
  const handleBiographyChange   = (e: React.ChangeEvent<HTMLTextAreaElement>) => { setBiography     (e.target.value); };
  const handleUrlChange         = (e: React.ChangeEvent<HTMLInputElement >) => { setPersonalPageUrl (e.target.value); };
  const handleAddressChange     = (e: React.ChangeEvent<HTMLInputElement >) => { setAddress         (e.target.value); };
  const handleZipCodeChange     = (e: React.ChangeEvent<HTMLInputElement >) => { setZipCode         (e.target.value); };
  const handleCityChange        = (e: React.ChangeEvent<HTMLInputElement >) => { setCity            (e.target.value); };
  const handleRegionChange      = (e: React.ChangeEvent<HTMLInputElement >) => { setRegion          (e.target.value); };
  const handleCountryChange     = (value: string) => { setCountry (value); };
  // Vérifier un créateur ne réactive que les comptes en attente : un compte suspendu ou
  // désactivé garde son statut, pour qu'on puisse bannir sans retirer la vérification.
  const handleVerifiedChange    = (newState: boolean) => { setVerified (newState); if (newState && accountStatus === 'pending') setAccountStatus ('active'); };
  const handleAccountStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => { setAccountStatus (e.target.value); };
  const handleUseFiligraneChange  = (newState: boolean) => { setUseFiligrane (newState); };
  const handleDurationMaxBroadcastingChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date) => {
    const seconds = timeStringToSeconds(String(value));
    setDurationMaxBroadcasting(seconds);
    if (!estDureeSansLimite(seconds)) { durationMaxBroadcastingAvantIllimite.current = seconds; }
  };
  const handleDurationMaxRecordingChange    = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date) => {
    const seconds = timeStringToSeconds(String(value));
    setDurationMaxRecording(seconds);
    if (!estDureeSansLimite(seconds)) { durationMaxRecordingAvantIllimite.current = seconds; }
  };
  // Bouton "Illimité" : bascule entre le mode limité (dernière valeur saisie,
  // restaurée) et le mode illimité (sentinelle 99:99:99 — cf. quota.ts).
  const handleDurationMaxBroadcastingUnlimitedToggle = (checked: boolean) => {
    setDurationMaxBroadcasting(checked ? DUREE_SANS_LIMITE_SECONDES : durationMaxBroadcastingAvantIllimite.current);
  };
  const handleDurationMaxRecordingUnlimitedToggle = (checked: boolean) => {
    setDurationMaxRecording(checked ? DUREE_SANS_LIMITE_SECONDES : durationMaxRecordingAvantIllimite.current);
  };

  // Remise à zéro périodique — un changement de fréquence retombe sur le jour 1 (mensuelle)
  // ou lundi (hebdomadaire) : l'ancien "jour" n'a pas forcément de sens dans le nouveau régime
  // (ex. jour 31 du mois en passant à hebdomadaire).
  const handleDurationMaxBroadcastingResetFrequencyChange = (value: string) => {
    durationMaxBroadcastingResetTouched.current = true;
    setDurationMaxBroadcastingResetFrequency(value as 'daily' | 'weekly' | 'monthly');
    setDurationMaxBroadcastingResetDay(1);
  };
  const handleDurationMaxBroadcastingResetDayChange  = (value: string) => { durationMaxBroadcastingResetTouched.current = true; setDurationMaxBroadcastingResetDay(Number(value)); };
  const handleDurationMaxBroadcastingResetTimeChange = (value: string) => { durationMaxBroadcastingResetTouched.current = true; setDurationMaxBroadcastingResetTime(timeStringToSeconds(value)); };

  const handleDurationMaxRecordingResetFrequencyChange = (value: string) => {
    durationMaxRecordingResetTouched.current = true;
    const frequency = value as 'daily' | 'weekly' | 'monthly';
    setDurationMaxRecordingResetFrequency(frequency);
    setDurationMaxRecordingResetDay(1);
  };
  const handleDurationMaxRecordingResetDayChange  = (value: string) => { durationMaxRecordingResetTouched.current = true; setDurationMaxRecordingResetDay(Number(value)); };
  const handleDurationMaxRecordingResetTimeChange = (value: string) => { durationMaxRecordingResetTouched.current = true; setDurationMaxRecordingResetTime(timeStringToSeconds(value)); };

  const handleQuotaDiskChange     = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date) => {
    const bytes = Math.round(Number(value) * (QUOTA_DISK_UNIT_DIVISORS[quotaDiskUnit] ?? 1));
    setQuotaDisk(bytes);
    if (!estSansLimite(bytes)) { quotaDiskAvantIllimite.current = bytes; }
  };
  const handleQuotaDiskUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => { setQuotaDiskUnit (e.target.value); };
  const handleNbDevicesMaxChange  = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date) => {
    const n = Number(value);
    setNbDevicesMax(n);
    if (!estSansLimite(n)) { nbDevicesMaxAvantIllimite.current = n; }
  };
  const handleNbChannelsMaxChange = (e: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date) => {
    const n = Number(value);
    setNbChannelsMax(n);
    if (!estSansLimite(n)) { nbChannelsMaxAvantIllimite.current = n; }
  };
  // Bouton "Illimité" : bascule entre le mode limité (dernière valeur saisie,
  // restaurée) et le mode illimité (sentinelle -1 — cf. quota.ts).
  const handleQuotaDiskUnlimitedToggle     = (checked: boolean) => { setQuotaDisk     (checked ? SANS_LIMITE : quotaDiskAvantIllimite.current); };
  const handleNbDevicesMaxUnlimitedToggle  = (checked: boolean) => { setNbDevicesMax  (checked ? SANS_LIMITE : nbDevicesMaxAvantIllimite.current); };
  const handleNbChannelsMaxUnlimitedToggle = (checked: boolean) => { setNbChannelsMax (checked ? SANS_LIMITE : nbChannelsMaxAvantIllimite.current); };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>, value: string | number | File) => {
    if (value instanceof File)
     {
      setPhotoUpload            (value);
      setPhotoMarkedForDeleting (false);
      setPhotoErrorMessage      ('');
     }
  };

  const handlePhotoDelete = () => {
    setPhotoUpload            (null);
    setPhotoMarkedForDeleting (true);
    setPhotoErrorMessage      ('');
    setPhotoResetKey          (key => key + 1);
  };



  const handlePasswordDraft1Change = (e: React.ChangeEvent<HTMLInputElement>) => { setPasswordDraft1 (e.target.value); setPasswordDraftError (''); };
  const handlePasswordDraft2Change = (e: React.ChangeEvent<HTMLInputElement>) => { setPasswordDraft2 (e.target.value); setPasswordDraftError (''); };

  const handleOpenPasswordWindow = () => {
    setPasswordDraft1     ('');
    setPasswordDraft2     ('');
    setPasswordDraftError ('');
    setShowPasswordWindow (true);
  };

  const handleCancelPasswordWindow = () => {
    setShowPasswordWindow (false);
  };

  const handleValidatePasswordWindow = () => {

    if (passwordDraft1 === '')             { setPasswordDraftError ("Le mot de passe ne peut pas être vide");   return; }
    if (passwordDraft1 !== passwordDraft2) { setPasswordDraftError ("Les mots de passe ne correspondent pas");  return; }

    setPassword             (passwordDraft1);
    setPasswordErrorMessage ('');
    setShowPasswordWindow   (false);
  };

  const saveMedias = async ( id: number ) => {

    if ( photoUpload !== null || photoMarkedForDeleting )
     {
      const photoResult = await updateAdminPhoto ( id , photoUpload , photoMarkedForDeleting );

      if ( photoResult.code !== 1 ) { setPhotoErrorMessage ( photoResult.comment || 'Erreur lors de la sauvegarde de la photo' ); return false; }

      setPhotoUpload            (null);
      setPhotoMarkedForDeleting (false);
     }

    if ( bannerUpload !== null || bannerMarkedForDeleting )
     {
      const bannerResult = await updateAdminBanner ( id , bannerUpload , bannerMarkedForDeleting );

      if ( bannerResult.code !== 1 ) { setBannerErrorMessage ( bannerResult.comment || 'Erreur lors de la sauvegarde du bandeau' ); return false; }

      setBannerUpload            (null);
      setBannerMarkedForDeleting (false);
     }

    return true;
  };

  // ── Nombre de modifications : nombre de champs dont la valeur actuelle diffère de sa valeur
  //    initiale (capturée une seule fois, au montage, via useRef — même valeurs que les useState
  //    ci-dessus). Recalculé à chaque rendu, pas de compteur incrémental : pas de dérive si un
  //    même champ est modifié plusieurs fois (même mécanisme que BroadcastingChannelConfig) ──
  const initialValuesRef = useRef({
    email, pseudonym, firstname, lastname, profileRole, biography, personalPageUrl,
    reporterAvailable, reporterCity, reporterRadiusKm,
    verified, accountStatus, useFiligrane, durationMaxBroadcasting, durationMaxRecording,
    durationMaxBroadcastingResetFrequency, durationMaxBroadcastingResetDay, durationMaxBroadcastingResetTime,
    durationMaxRecordingResetFrequency, durationMaxRecordingResetDay, durationMaxRecordingResetTime,
    quotaDisk, nbDevicesMax, nbChannelsMax,
    address, zipCode, city, region, country,
  });
  const initialValues = initialValuesRef.current;

  const nbMods = (() => {
    let n = 0;
    if (email           !== initialValues.email          ) n++;
    if (password         !== '') n++;
    if (pseudonym        !== initialValues.pseudonym      ) n++;
    if (firstname        !== initialValues.firstname      ) n++;
    if (lastname         !== initialValues.lastname       ) n++;
    if (profileRole      !== initialValues.profileRole    ) n++;
    if (biography        !== initialValues.biography      ) n++;
    if (personalPageUrl  !== initialValues.personalPageUrl) n++;
    if (reporterAvailable !== initialValues.reporterAvailable) n++;
    if (reporterCity      !== initialValues.reporterCity     ) n++;
    if (reporterRadiusKm  !== initialValues.reporterRadiusKm ) n++;
    if (verified         !== initialValues.verified       ) n++;
    if (accountStatus    !== initialValues.accountStatus  ) n++;
    if (useFiligrane     !== initialValues.useFiligrane   ) n++;
    if (durationMaxBroadcasting !== initialValues.durationMaxBroadcasting) n++;
    if (durationMaxRecording    !== initialValues.durationMaxRecording   ) n++;
    if (durationMaxBroadcastingResetFrequency !== initialValues.durationMaxBroadcastingResetFrequency) n++;
    if (durationMaxBroadcastingResetDay       !== initialValues.durationMaxBroadcastingResetDay      ) n++;
    if (durationMaxBroadcastingResetTime      !== initialValues.durationMaxBroadcastingResetTime     ) n++;
    if (durationMaxRecordingResetFrequency    !== initialValues.durationMaxRecordingResetFrequency   ) n++;
    if (durationMaxRecordingResetDay          !== initialValues.durationMaxRecordingResetDay         ) n++;
    if (durationMaxRecordingResetTime         !== initialValues.durationMaxRecordingResetTime        ) n++;
    if (quotaDisk        !== initialValues.quotaDisk      ) n++;
    if (nbDevicesMax     !== initialValues.nbDevicesMax   ) n++;
    if (nbChannelsMax    !== initialValues.nbChannelsMax  ) n++;
    if (address          !== initialValues.address        ) n++;
    if (zipCode          !== initialValues.zipCode        ) n++;
    if (city             !== initialValues.city           ) n++;
    if (region           !== initialValues.region         ) n++;
    if (country          !== initialValues.country        ) n++;
    if (photoUpload || photoMarkedForDeleting) n++;
    if (bannerUpload || bannerMarkedForDeleting) n++;
    return n;
  })();

  // ── Choix de statut proposés : « En attente » disparaît dès que le créateur est vérifié.
  //    La combinaison est refusée côté API (AdminUpdate), qui la ramènerait à « Actif » ──
  const accountStatusOptions = ACCOUNT_STATUS_VALUES
        .map    ( ( value , i ) => ( { value , label : ACCOUNT_STATUS_LABELS [ i ] } ) )
        .filter ( ( { value }  ) => !( verified && value === 'pending' ) );

  const profilePayload = () => (
   {
    email           ,
    ...(password !== '' ? { password } : {}),
    pseudonym       ,
    slug            ,
    firstname       ,
    lastname        ,
    biography       ,
    personalPageUrl ,
    reporterAvailable ,
    reporterCity    : reporterCity.trim() ,
    reporterRadiusKm : reporterRadiusKm.trim() !== '' ? Number(reporterRadiusKm) : null ,
    profileRole     ,
    verified        ,
    accountStatus   ,
    useFiligrane    ,
    durationMaxBroadcasting,
    durationMaxRecording   ,
    // quotaDisk/nbDevicesMax/nbChannelsMax partent désormais des defaults métier (2/1/2 Go,
    // cf. QUOTA_DISK_DEFAUT ci-dessus) même en création, pas de 0 arbitraire : les envoyer
    // systématiquement ne risque plus d'écraser les defaults d'AdminCreation avec un
    // formulaire vierge — le formulaire montre déjà la valeur qui sera enregistrée.
    quotaDisk       ,
    nbDevicesMax    ,
    nbChannelsMax   ,
    // Jour/heure de reset : en édition, ils reflètent déjà le compte réel, donc toujours
    // envoyés. En création, le formulaire les affiche figés (jour 1, minuit) sans rapport
    // avec l'instant réel de création — les envoyer par défaut écraserait le calcul
    // d'AdminCreation aligné sur cet instant. On ne les envoie donc que si l'opérateur y a
    // explicitement touché (cf. durationMax{Broadcasting,Recording}ResetTouched).
    ...((isEditMode || durationMaxBroadcastingResetTouched.current) ? {
      durationMaxBroadcastingResetFrequency,
      durationMaxBroadcastingResetDay      ,
      durationMaxBroadcastingResetTime     ,
    } : {}),
    ...((isEditMode || durationMaxRecordingResetTouched.current) ? {
      durationMaxRecordingResetFrequency,
      durationMaxRecordingResetDay      ,
      durationMaxRecordingResetTime     ,
    } : {}),
    address         ,
    zipCode         ,
    city            ,
    region          ,
    country         ,
   });

  // ── Enregistrement (mode édition) ──
  const handleSaveButton = async () => {

    if (!admin || saveButtonLock || pseudonymErrorMessage !== '' || nbMods === 0) return;

    const emailErrorMessageBis = email === '' ? "Il manque l'e-mail" : '';
    setEmailErrorMessage(emailErrorMessageBis);
    if (emailErrorMessageBis !== '') return;

    setSaveButtonLock(true);

    const result   = await updateAdmin ( admin.id , profilePayload() );
    const mediasOk = await saveMedias  ( admin.id );

    // La fiche éditée peut être celle du compte actuellement connecté (un superadmin
    // gère aussi sa propre fiche depuis cet écran) : sans ce rechargement, le header
    // (nbDevicesMax, quotaDisk, …) reste figé sur les valeurs du login jusqu'à la
    // reconnexion suivante, alors que la BDD est déjà à jour — même mécanisme que
    // refreshAdmin() après une bascule de chaîne (BroadcastingChannelSheet/List).
    if (result.code === 1 && authentication.admin?.email && admin.id === authentication.admin.id) {
      dispatch(refreshAdmin(authentication.admin.email));
    }

    setSaved(true);
    setError(result.code !== 1 || !mediasOk);

    if (result.code !== 1) { setErrorMsg(result.comment); }
    else                   { setPassword(''); }

    setSaveButtonLock(false);
  };

  // ── Création ──
  const handleCreateButton = async () => {

    if (saveButtonLock) return;

    // Mot de passe/prénom/nom ne sont bloquants ici que dans le cas courant (nouveau
    // compte) — mais tant qu'on n'a pas tenté l'appel, on ne sait pas encore si cet e-mail
    // correspond à un compte client existant (promotion), auquel cas AdminCreation n'exige
    // ni l'un ni l'autre (son propre mot de passe et son Person existants font foi). Les
    // bloquer ici de toute façon reproduirait le blocage silencieux qu'on corrige : e-mail
    // saisi, rien d'autre requis pour tenter — le backend renvoie 400 avec le champ manquant
    // s'il s'avère que c'est bien un nouveau compte, ou la question de promotion sinon.
    const emailErrorMessageBis = email === '' ? "Il manque l'e-mail" : '';

    setEmailErrorMessage (emailErrorMessageBis);

    const test = emailErrorMessageBis === '' && pseudonymErrorMessage === '';

    if (!test) return;

    // Mot de passe vide : on demande confirmation avant d'envoyer quoi que ce soit, plutôt
    // que de générer silencieusement un mot de passe côté serveur (cf.
    // handleNoPasswordConfirmation ci-dessous).
    if (password === '') { setShowNoPasswordConfirmation(true); return; }

    setSaveButtonLock(true);

    await submitAdminCreation(false);
  };

  // Rappelé après confirmation de l'absence de mot de passe (cf. showNoPasswordConfirmation
  // ci-dessus) : password reste '' dans profilePayload(), donc AdminCreation en génère un.
  const handleNoPasswordConfirmation = async (confirmed: boolean) => {

    setShowNoPasswordConfirmation(false);

    if (!confirmed) return;

    setSaveButtonLock(true);

    await submitAdminCreation(false);
  };

  // isPromotion: true seulement sur le rappel qui suit la confirmation de l'utilisateur
  // (cf. showPromotionConfirmation ci-dessous) — jamais posé au premier essai.
  const submitAdminCreation = async (isPromotion: boolean) => {

    const result = await createAdmin(email, password, firstname, lastname, profilePayload(), isPromotion, authentication.admin?.id);

    if (result.code === 1)
     {
      await saveMedias(result.payload);

      setSaved(true);
      setError(false);
      setSaveButtonLock(false);
     }
    else if (result.code === PROMOTION_CONFIRMATION_REQUIRED_CODE)
     {
      setShowPromotionConfirmation(true);
      setSaveButtonLock(false);
     }
    else
     {
      setSaved(true);
      setError(true);
      setErrorMsg(result.comment);
      setSaveButtonLock(false);
     }
  };

  const handlePromotionConfirmation = async (confirmed: boolean) => {

    setShowPromotionConfirmation(false);

    if (!confirmed) return;

    setSaveButtonLock(true);

    await submitAdminCreation(true);
  };

  const handleInformationWindowClosure = () => {

    setSaved(false);

    if (error) return;

    if (!isEditMode && onCreate)          onCreate(0);
    if ( isEditMode && admin   && onEdit) onEdit(admin.id);
  };

  const handleReturnButton = () => {

    if (isEditMode && admin && onEdit) onEdit(admin.id);
  };

  const handleUsageResetButton = (field: AdminUsageCounterField) => {

    if (!admin || usageResetLockedFields[field]) return;

    setUsageResetPendingField(field);
  };

  const handleUsageResetConfirmation = async (confirmed: boolean) => {

    const field = usageResetPendingField;

    setUsageResetPendingField(null);

    if (!confirmed || !admin || !field) return;

    setUsageResetLockedFields(prev => ({ ...prev, [field]: true }));

    setUsageResetInProgress(true);

    let result: Awaited<ReturnType<typeof resetAdminUsageCounters>>;

    try {
      result = await resetAdminUsageCounters(admin.id, field);
    } finally {
      setUsageResetInProgress(false);
      setUsageResetLockedFields(prev => ({ ...prev, [field]: false }));
    }

    const deviceCountSuffix = result.code === 1 && typeof result.payload?.disconnectedDevices === 'number'
      ? ` (${result.payload.disconnectedDevices} appareil${result.payload.disconnectedDevices > 1 ? 's' : ''} déconnecté${result.payload.disconnectedDevices > 1 ? 's' : ''})`
      : '';

    setUsageResetResult({
      error   : result.code !== 1,
      message : result.code === 1 ? `Compteur réinitialisé.${deviceCountSuffix}` : result.comment,
    });
  };

  // Comme handleInformationWindowClosure : recharge la fiche depuis le parent pour que
  // les compteurs affichés (issus directement de `admin`, jamais d'un état local ici)
  // reflètent la remise à zéro tout juste effectuée.
  const handleUsageResetResultClosure = () => {

    setUsageResetResult(null);

    if (admin && onEdit) onEdit(admin.id);
  };

  return (
    <div className={classNames(styles.root)}>

      {
       saved &&
       (
        <Window message                 = {
                                           error
                                           ? <>Une erreur est survenue lors de {isEditMode ? 'la mise à jour' : 'la création'} du compte{errorMsg ? <> : {errorMsg}</> : null}</>
                                           : <>{isEditMode ? 'Compte mis à jour' : 'Compte créé'}</>
                                          }
                messageType             = "information"
                validButtonText         = "Ok"
                onClickOnValidateButton = {handleInformationWindowClosure} />
       )
      }

      {
       showPromotionConfirmation &&
       (
        <Window message                 = {<>Un compte existe déjà avec cet e-mail. Voulez-vous le transformer en compte Créateur&nbsp;?</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnValidateButton = {() => handlePromotionConfirmation(true)}
                onClickOnCancelButton   = {() => handlePromotionConfirmation(false)} />
       )
      }

      {
       showNoPasswordConfirmation &&
       (
        <Window message                 = {<>Vous n'avez pas spécifié de mot de passe, génération automatique par le système&nbsp;?</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnValidateButton = {() => handleNoPasswordConfirmation(true)}
                onClickOnCancelButton   = {() => handleNoPasswordConfirmation(false)} />
       )
      }

      {usageResetInProgress && <AnimatedWaitingIcon2 />}

      {
       usageResetPendingField &&
       (
        <Window message                 = {<>Réinitialiser à zéro {USAGE_RESET_LABELS[usageResetPendingField]} de ce créateur&nbsp;? Le plafond n'est pas touché.{usageResetPendingField === 'nbDevicesUsed' && <> Les appareils encore connectés sur ses chaînes seront déconnectés.</>}</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnValidateButton = {() => handleUsageResetConfirmation(true)}
                onClickOnCancelButton   = {() => handleUsageResetConfirmation(false)} />
       )
      }

      {
       usageResetResult &&
       (
        <Window message                 = {usageResetResult.error ? <>Une erreur est survenue lors de la réinitialisation{usageResetResult.message ? <> : {usageResetResult.message}</> : null}</> : <>{usageResetResult.message}</>}
                messageType             = "information"
                validButtonText         = "Ok"
                onClickOnValidateButton = {handleUsageResetResultClosure} />
       )
      }

      {
       showPasswordWindow &&
       (
        <>
          <div className={classNames(styles.passwordOverlay)} />
          <div className={classNames(styles.passwordWindow)}>

            <div className={classNames(styles.passwordWindowTitle)}>Nouveau mot de passe</div>

            <Field type="password" placeholder="Mot de passe"  value={passwordDraft1} onChange={handlePasswordDraft1Change} eyeVisible={true} />
            <Field type="password" placeholder="Confirmation"  value={passwordDraft2} onChange={handlePasswordDraft2Change} eyeVisible={true} />

            {
             passwordDraftError !== '' &&
             <p className={classNames(styles.alert)}>{passwordDraftError}</p>
            }

            <div className={classNames(styles.passwordWindowButtons)}>
              <Trigger name="Annuler" onClick={handleCancelPasswordWindow}    variant="tailwind2" />
              <Trigger name="Valider" onClick={handleValidatePasswordWindow} variant="tailwind2" />
            </div>

          </div>
        </>
       )
      }

      <div className={classNames(styles.head)}>
        <span className={classNames(styles.headIcon)}>
          <Icon name="User" color="#4992FA" hoverable={false} clickable={false} width={20} height={20} />
        </span>
        <div className={classNames(styles.headTitleBlock)}>
          <span className={classNames(styles.headTitle)}>{isEditMode ? 'Compte créateur' : 'Nouveau compte créateur'}</span>
          <span className={classNames(styles.headSubtitle)}>
            {/*isEditMode ? (pseudonym || `${firstname} ${lastname}`.trim() || email || '—') : "Création d'un nouveau compte"*/}
			Créez ou modifiez les informations du comptes créateur
          </span>
        </div>
        <div className={classNames(styles.headActions)}>
          {
           nbMods > 0 &&
           <span className={classNames(styles.modBadge)}>{nbMods} modification{nbMods > 1 ? 's' : ''}</span>
          }
          {
           isEditMode
           ? <>
               <button className={classNames(styles.btn, styles.btnOutline)} onClick={handleReturnButton}>Retour</button>
               <button className={classNames(styles.btn, nbMods > 0 ? styles.btnSave : styles.btnPrimary)}
                       onClick={handleSaveButton}
                       disabled={saveButtonLock}>
                 <SaveIcon/>
                 {saveButtonLock ? 'Enregistrement…' : 'Enregistrer'}{!saveButtonLock && nbMods > 0 && ` (${nbMods})`}
               </button>
             </>
           : <button className={classNames(styles.btn, nbMods > 0 ? styles.btnSave : styles.btnPrimary)}
                     onClick={handleCreateButton}
                     disabled={saveButtonLock}>
               <SaveIcon/>
               {saveButtonLock ? 'Création…' : 'Créer'}{!saveButtonLock && nbMods > 0 && ` (${nbMods})`}
             </button>
          }
        </div>
      </div>

      {
       (() => {

        const infoBlock = (
         <>

      <div className={classNames(styles.block)}>

        <div className={classNames(styles.metaGrid)}>
          <WidgetFrame name                  = "ID :"
                       labelTextPosition     = "left"
                       labelBlockPosition    = "left"
                       childrenBlockPosition = "left"
					   labelWidth            = '30px' 
                       classNameGrid         = {classNames(styles.grid_frame)}>
            <p className={classNames(styles.text)}>{isEditMode && admin ? admin.id : '—'}</p>
          </WidgetFrame>

          <WidgetFrame name                  = "UUID :"
                       labelTextPosition     = "left"
                       labelBlockPosition    = "left"
                       childrenBlockPosition = "left"
                       labelWidth            = '50px' 
                       classNameGrid         = {classNames(styles.grid_frame)}>
            <p className={classNames(styles.text)}>{(isEditMode && admin?.uuid) || '—'}</p>
          </WidgetFrame>

          <WidgetFrame name                  = "Date de création :"
                       labelTextPosition     = "left"
                       labelBlockPosition    = "left"
                       childrenBlockPosition = "left"
                       labelWidth            = '130px' 
                       classNameGrid         = {classNames(styles.grid_frame)}>
            <p className={classNames(styles.text)}>{isEditMode && admin ? formatDateOrDash(admin.creationDate) : '—'}</p>
          </WidgetFrame>

          <WidgetFrame name                  = "Dernière modification :"
                       labelTextPosition     = "left"
                       labelBlockPosition    = "left"
                       childrenBlockPosition = "left"
                       labelWidth            = '165px' 
                       classNameGrid         = {classNames(styles.grid_frame)}>
            <p className={classNames(styles.text)}>{isEditMode && admin ? formatDateOrDash(admin.lastModificationDate) : '—'}</p>
          </WidgetFrame>
        </div>
      </div>

      {/* ══ Grille du haut : publiques (pleine largeur) / privées + localisation (gauche) / statut (droite, pleine hauteur) ══ */}
      <div className={classNames(styles.topGrid)}>

      <div className={classNames(styles.block, styles.areaPublic)}>
        <div className={classNames(styles.blockTitle)}><div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="publicPersonnalInfos" color="#2563EB" hoverable={false} clickable={false}/> <span>Informations publiques</span> </div></div>

        <div className={classNames(styles.photoRow)}>

          <div className={classNames(styles.photoThumb)}>
            <WidgetFrame name                  = {
                                                   <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                     <span>Miniature :</span>
                                                     <div className={classNames(styles.iconSquare)} onClick={handlePhotoDelete}>
                                                       <Icon name="trashCan" color="#FFFFFF" hoverColor="#FF0000" width={16} height={16} />
                                                     </div>
                                                   </div>
                                                  }
                         labelTextPosition     = "left"
                         labelBlockPosition    = "top"
                         classNameGrid         = {classNames(styles.grid_frame)}>
              <>
                <FieldMedia key                = {photoResetKey}
                            placeholder        = ""
                            value              = {photoMarkedForDeleting ? undefined : (isEditMode && admin ? getAdminPhotoUrl(admin) : undefined)}
                            showTextInput      = {false}
                            upload             = {false}
                            showControlsRow    = {false}
                            previewAlwaysShown = {true}
                            previewWidth       = "98px"
                            previewHeight      = "98px"
                            defaultImage       = {defaultPhotoImage}
                            onChange           = {handlePhotoChange}
                            previewShape       = 'circle'
                            previewBordered    = {true}/>
                {
                 photoErrorMessage !== '' &&
                 <p className={classNames(styles.alert)}>{photoErrorMessage}</p>
                }
              </>
            </WidgetFrame>
          </div>

          <div className={classNames(styles.photoRowFields)}>
            <WidgetFrame name="Nom public (ou pseudonyme) :" error={pseudonymErrorMessage} labelTextPosition="left" labelBlockPosition="top" classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" placeholder="" value={pseudonym} onChange={handlePseudonymChange} />
            </WidgetFrame>

            <WidgetFrame name="URL de la page perso :" labelTextPosition="left" labelBlockPosition="top" classNameGrid={classNames(styles.grid_frame)}>
              <Field type="text" placeholder="https://…" value={personalPageUrl} onChange={handleUrlChange} />
            </WidgetFrame>
          </div>

          <div className={classNames(styles.photoRowFields)}>
            <WidgetFrame name="Reporter Kleon (sollicitable pour un événement) :" labelTextPosition="left" labelBlockPosition="top" childrenBlockPosition="right" classNameGrid={classNames(styles.grid_frame)}>
              <SwitchOnOff checked={reporterAvailable} onChange={() => setReporterAvailable(v => !v)} ariaLabel="Reporter Kleon" />
            </WidgetFrame>

            {reporterAvailable && (
              <>
                <WidgetFrame name="Ville de départ :" labelTextPosition="left" labelBlockPosition="top" classNameGrid={classNames(styles.grid_frame)}>
                  <Field type="text" placeholder="Lyon" value={reporterCity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReporterCity(e.target.value)} />
                </WidgetFrame>
                <WidgetFrame name="Rayon de déplacement (km) :" labelTextPosition="left" labelBlockPosition="top" classNameGrid={classNames(styles.grid_frame)}>
                  <Field type="text" placeholder="50" value={reporterRadiusKm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReporterRadiusKm(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                </WidgetFrame>
              </>
            )}
          </div>

          <div className={classNames(styles.biographyCol)}>
            <WidgetFrame name="Description publique :" labelTextPosition="left" labelBlockPosition="top" classNameGrid={classNames(styles.grid_frame)}>
              <TextArea placeholder="" value={biography} onChange={handleBiographyChange} height="96px" />
            </WidgetFrame>
          </div>

        </div>
      </div>

        <div className={classNames(styles.block, styles.areaPrive)}>
          <div className={classNames(styles.blockTitle)}><div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="privatePersonnalInfos" color="#2563EB" hoverable={false} clickable={false}/> <span>Informations privées</span> </div></div>

              <div className={classNames(styles.nameRow)}>
                <WidgetFrame name               = "Prénom :" 
				             error              = {firstnameErrorMessage} 
							 labelTextPosition  = "left" 
							 labelBlockPosition = "left" 
						     labelWidth         = '70px' 
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text" 
				         placeholder = "" 
						 value       = {firstname} 
						 onChange    = {handleFirstnameChange} />
                </WidgetFrame>

                <WidgetFrame name               = "Nom :" 
				             error              = {lastnameErrorMessage} 
							 labelTextPosition  = "left" 
							 labelBlockPosition = "left" 
						     labelWidth         = '45px' 
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text" 
				         placeholder = "" 
						 value       = {lastname} 
						 onChange    = {handleLastnameChange} />
                </WidgetFrame>
              </div>

              <div className={classNames(styles.nameRow)}>
                <WidgetFrame name               = "Email :"
				             error              = {emailErrorMessage}
							 labelTextPosition  = "left"
							 labelBlockPosition = "left"
						     labelWidth         = '70px'
							 labelVerticalAlign = "center"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text"
					     placeholder = ""
						 value       = {email}
						 onChange    = {handleEmailChange} />
                </WidgetFrame>

                <div className={classNames(styles.btnPass)}>
                  <Trigger name    = 'mot de passe'
				           onClick = {handleOpenPasswordWindow}
						   variant = "tailwind2" />
                </div>
              </div>

        </div>

        <div className={classNames(styles.block, styles.areaLocal)}>
          <div className={classNames(styles.blockTitle)}><div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="localisation" color="#2563EB" hoverable={false} clickable={false}/> <span>Localisation</span> </div></div>

          <WidgetFrame name               = "Adresse :" 
		               labelTextPosition  = "left" 
					   labelBlockPosition = "left" 
					   labelWidth         = '96px' 
					   classNameGrid      = {classNames(styles.grid_frame)}>
            <Field type        = "text" 
			       placeholder = "" 
				   value       = {address} 
				   onChange    = {handleAddressChange} />
          </WidgetFrame>

          <div className={classNames(styles.nameRow)}>
            <WidgetFrame name               = "Code postal :" 
			             labelTextPosition  = "left" 
						 labelBlockPosition = "left" 
						 labelWidth         = '96px' 
					     classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type        = "text" 
			         placeholder = "" 
					 value       = {zipCode} 
					 onChange    = {handleZipCodeChange} />
            </WidgetFrame>

            <WidgetFrame name               = "Ville :" 
			             labelTextPosition  = "left" 
						 labelBlockPosition = "left" 
						 labelWidth         = '45px' 
						 classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type        = "text" 
			         placeholder = "" 
					 value       = {city} 
					 onChange    = {handleCityChange} />
            </WidgetFrame>
          </div>

          <div className={classNames(styles.nameRow)}>
            <WidgetFrame name               = "Région :" 
			             labelTextPosition  = "left" 
						 labelBlockPosition = "left" 
						 labelWidth         = '96px' 
						 classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type        = "text" 
			         placeholder = "" 
					 value       = {region} 
					 onChange    = {handleRegionChange} />
            </WidgetFrame>

            <WidgetFrame name               = "Pays :" 
			             labelTextPosition  = "left" 
						 labelBlockPosition = "left" 
						 labelWidth         = '45px' 
						 classNameGrid      = {classNames(styles.grid_frame)}>
              <CountrySelector value={country}
                               placeholder="—"
                               onChange={handleCountryChange} />
            </WidgetFrame>
          </div>

        </div>

        <div className={classNames(styles.block, styles.areaStatut)}>
            <div className={classNames(styles.blockTitle)}><div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="growth" color="#2563EB" hoverable={false} clickable={false}/> <span>Statut et activité</span> </div></div>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="verified" color="#2563EB" hoverable={false} clickable={false}/> <span>Créateur vérifié :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>
              <Toggle checked={verified} onChange={handleVerifiedChange} readOnly={!isEditMode} form="switch" />
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="growth" color="#2563EB" hoverable={false} clickable={false}/> <span>Statut du compte :</span> </div>}
			             labelTextPosition     = "left"
						 labelBlockPosition    = "left"
						 childrenBlockPosition = "right"
						 classNameGrid         = {classNames(styles.grid_frame)}>
              <Select choices          = {accountStatusOptions.map ( o => o.value )}
                      choicesLabel     = {accountStatusOptions.map ( o => o.label )}
                      value            = {accountStatus}
                      showPlaceholder  = {false}
                      disabled         = {!isEditMode}
                      onChange         = {handleAccountStatusChange} />
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="image" color="#2563EB" hoverable={false} clickable={false}/> <span>Filigrane :</span> </div>}
			             labelTextPosition     = "left"
						 labelBlockPosition    = "left"
						 childrenBlockPosition = "right"
						 classNameGrid         = {classNames(styles.grid_frame)}>
              <Toggle checked={useFiligrane} onChange={handleUseFiligraneChange} form="switch" />
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="subscribers" color="#2563EB" hoverable={false} clickable={false}/> <span>Nombre d'abonnés :</span> </div>}
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>
              <p className={classNames(styles.text)}>{formatSubscriberCount(isEditMode && admin ? admin.nbSubscribers : 0)}</p>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="clock" color="#2563EB" hoverable={false} clickable={false}/> <span>Dernière connexion :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>
              <p className={classNames(styles.text)}>{isEditMode && admin ? formatDateOrDash(admin.lastLoginDate) : '—'}</p>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="internet" color="#2563EB" hoverable={false} clickable={false}/> <span>Dernière IP :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>

              <p className={classNames(styles.text)}>{(isEditMode && admin?.lastLoginIP) || '—'}</p>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="pinOnMap" color="#2563EB" hoverable={false} clickable={false}/> <span>Ville de connexion :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>

              <p className={classNames(styles.text)}>{(isEditMode && admin?.lastLoginCity) || '—'}</p>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="devices" color="#2563EB" hoverable={false} clickable={false}/> <span>Appareils :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>

              <div className={classNames(styles.usedOverTotal)}>
                {isEditMode && admin &&
                  <button type="button" className={classNames(styles.usageResetBtn)}
                          title="Réinitialiser le nombre d'appareils"
                          disabled={!!usageResetLockedFields.nbDevicesUsed}
                          onClick={() => handleUsageResetButton('nbDevicesUsed')}>
                    <Icon name="undo" width={12} height={12} color="#2563EB" hoverable={false} clickable={false} />
                  </button>
                }
                <span className={classNames(styles.text)}>{isEditMode && admin ? admin.nbDevicesUsed : 0}</span>
                {!estSansLimite(nbDevicesMax) && <>
                  <span className={classNames(styles.text)}>/</span>
                  <Field type="number" decimalPlaces={0} width="45px" value={nbDevicesMax} onChange={handleNbDevicesMaxChange} />
                </>}
                <UnlimitedBadge active={estSansLimite(nbDevicesMax)} onClick={() => handleNbDevicesMaxUnlimitedToggle(!estSansLimite(nbDevicesMax))} />
              </div>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="tv1" color="#2563EB" hoverable={false} clickable={false}/> <span>Chaînes :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>

              <div className={classNames(styles.usedOverTotal)}>
                <span className={classNames(styles.text)}>{isEditMode && admin ? admin.nbChannelsUsed : 0}</span>
                {!estSansLimite(nbChannelsMax) && <>
                  <span className={classNames(styles.text)}>/</span>
                  <Field type="number" decimalPlaces={0} width="45px" value={nbChannelsMax} onChange={handleNbChannelsMaxChange} />
                </>}
                <UnlimitedBadge active={estSansLimite(nbChannelsMax)} onClick={() => handleNbChannelsMaxUnlimitedToggle(!estSansLimite(nbChannelsMax))} />
              </div>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="storage" color="#2563EB" hoverable={false} clickable={false}/> <span>Quota disque :</span> </div>} 
			             labelTextPosition     = "left" 
						 labelBlockPosition    = "left" 
						 childrenBlockPosition = "right" 
						 classNameGrid         = {classNames(styles.grid_frame)}>

              <div className={classNames(styles.usedOverTotal)}>
                <span className={classNames(styles.text)}>{((isEditMode && admin ? admin.quotaUsed : 0) / (QUOTA_DISK_UNIT_DIVISORS[quotaDiskUnit] ?? 1)).toFixed(quotaDiskUnit === 'octets' ? 0 : 2)}</span>
                {!estSansLimite(quotaDisk) && <>
                  <span className={classNames(styles.text)}>/</span>
                  <Field type="number" decimalPlaces={quotaDiskUnit === 'octets' ? 0 : 2} width="120px" value={quotaDisk / (QUOTA_DISK_UNIT_DIVISORS[quotaDiskUnit] ?? 1)} onChange={handleQuotaDiskChange} />
                </>}
                <Select choices={QUOTA_DISK_UNIT_CHOICES} value={quotaDiskUnit} width="80px" showPlaceholder={false} onChange={handleQuotaDiskUnitChange} />
                <UnlimitedBadge active={estSansLimite(quotaDisk)} onClick={() => handleQuotaDiskUnlimitedToggle(!estSansLimite(quotaDisk))} />
              </div>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="clock" color="#2563EB" hoverable={false} clickable={false}/> <span>Durée diffusion :</span> </div>}
				             labelTextPosition     = "left"
							 labelBlockPosition    = "left"
							 childrenBlockPosition = "right"
							 classNameGrid         = {classNames(styles.grid_frame)}>
              <div className={classNames(styles.durationWithReset)}>
                <div className={classNames(styles.usedOverTotal)}>
                  {isEditMode && admin &&
                    <button type="button" className={classNames(styles.usageResetBtn)}
                            title="Réinitialiser la durée de diffusion"
                            disabled={!!usageResetLockedFields.durationElapsedBroadcasting}
                            onClick={() => handleUsageResetButton('durationElapsedBroadcasting')}>
                      <Icon name="undo" width={12} height={12} color="#2563EB" hoverable={false} clickable={false} />
                    </button>
                  }
                  <span className={classNames(styles.text)}>{formatHms(isEditMode && admin ? admin.durationElapsedBroadcasting : 0)}</span>
                  {!estDureeSansLimite(durationMaxBroadcasting) && <>
                    <span className={classNames(styles.text)}>/</span>
                    <Field type="text" width="90px" value={formatHms(durationMaxBroadcasting)} onChange={handleDurationMaxBroadcastingChange} />
                  </>}
                  <UnlimitedBadge active={estDureeSansLimite(durationMaxBroadcasting)} onClick={() => handleDurationMaxBroadcastingUnlimitedToggle(!estDureeSansLimite(durationMaxBroadcasting))} />
                </div>
                {!estDureeSansLimite(durationMaxBroadcasting) &&
                  <ResetScheduleRow frequency         = {durationMaxBroadcastingResetFrequency}
                                    day               = {durationMaxBroadcastingResetDay}
                                    time              = {durationMaxBroadcastingResetTime}
                                    onFrequencyChange = {handleDurationMaxBroadcastingResetFrequencyChange}
                                    onDayChange       = {handleDurationMaxBroadcastingResetDayChange}
                                    onTimeChange      = {handleDurationMaxBroadcastingResetTimeChange} />}
              </div>
            </WidgetFrame>

            <WidgetFrame name                  = {<div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="clock" color="#2563EB" hoverable={false} clickable={false}/> <span>Durée enregistrement :</span> </div>}
				             labelTextPosition     = "left"
							 labelBlockPosition    = "left"
							 childrenBlockPosition = "right"
							 classNameGrid         = {classNames(styles.grid_frame)}>
              <div className={classNames(styles.durationWithReset)}>
                <div className={classNames(styles.usedOverTotal)}>
                  {isEditMode && admin &&
                    <button type="button" className={classNames(styles.usageResetBtn)}
                            title="Réinitialiser la durée d'enregistrement"
                            disabled={!!usageResetLockedFields.durationElapsedRecording}
                            onClick={() => handleUsageResetButton('durationElapsedRecording')}>
                      <Icon name="undo" width={12} height={12} color="#2563EB" hoverable={false} clickable={false} />
                    </button>
                  }
                  <span className={classNames(styles.text)}>{formatHms(isEditMode && admin ? admin.durationElapsedRecording : 0)}</span>
                  {!estDureeSansLimite(durationMaxRecording) && <>
                    <span className={classNames(styles.text)}>/</span>
                    <Field type="text" width="90px" value={formatHms(durationMaxRecording)} onChange={handleDurationMaxRecordingChange} />
                  </>}
                  <UnlimitedBadge active={estDureeSansLimite(durationMaxRecording)} onClick={() => handleDurationMaxRecordingUnlimitedToggle(!estDureeSansLimite(durationMaxRecording))} />
                </div>
                {!estDureeSansLimite(durationMaxRecording) &&
                  <ResetScheduleRow frequency         = {durationMaxRecordingResetFrequency}
                                    day               = {durationMaxRecordingResetDay}
                                    time              = {durationMaxRecordingResetTime}
                                    onFrequencyChange = {handleDurationMaxRecordingResetFrequencyChange}
                                    onDayChange       = {handleDurationMaxRecordingResetDayChange}
                                    onTimeChange      = {handleDurationMaxRecordingResetTimeChange} />}
              </div>
            </WidgetFrame>

          </div>

          {
           isEditMode && admin &&
           (
            <div className={classNames(styles.block, styles.areaSocial)}>
              <div className={classNames(styles.blockTitle)}><div style={{ display: "flex", alignItems: "center", gap: "10px" }} > <Icon name="internet" color="#2563EB" hoverable={false} clickable={false}/> <span>Réseaux sociaux</span> </div></div>

              <div className={classNames(styles.socialNetworkList)}>
                {
                 socialNetworks.map(network => {
                  // Deuxième chemin de connexion, propre à Instagram : affiché
                  // seulement tant qu'Instagram n'est pas connecté et que
                  // Facebook, lui, l'est déjà — inutile sinon (cf.
                  // handleLinkInstagramViaFacebook).
                  const facebookConnected = network.code === 'instagram' &&
                    socialNetworks.some(n => n.code === 'facebook' && n.connected);

                  return (
                  <div key={network.socialNetworkId}
                       className={classNames(styles.socialNetworkRow, !network.implemented && styles.socialNetworkRow_disabled)}>
                    <span className={classNames(styles.socialNetworkName)}>{network.name}</span>

                    {
                     network.connected &&
                     <span className={classNames(styles.socialNetworkAccount)}>{network.externalAccountLabel ?? 'Connecté'}</span>
                    }

                    <div className={classNames(styles.socialNetworkActions)}>
                      {
                       !network.implemented
                       ? <span className={classNames(styles.socialNetworkAccount)}>Bientôt disponible</span>
                       : network.connected
                         ? <Trigger name="Déconnecter" variant="flat" onClick={() => handleDisconnectSocialNetwork(network.adminSocialNetworkId!)} />
                         : <>
                             <Trigger name="Connecter" variant="flat" onClick={() => handleConnectSocialNetwork(network.code)} />
                             {
                              facebookConnected &&
                              <Trigger name="Lier via Facebook" variant="flat" onClick={handleLinkInstagramViaFacebook} />
                             }
                           </>
                      }
                    </div>
                  </div>
                  );
                 })
                }
              </div>
            </div>
           )
          }

      </div>

         </>
        );

        if (!isEditMode || !admin) { return infoBlock; }

        // La fiche créateur reprend le principe des chaînes : un premier onglet
        // "Tableau de bord" (les infos déjà là) puis les sections Achats/Abonnements
        // du créateur, qui n'apparaissent donc plus comme entrées séparées du menu
        // latéral (cf. desktop.tsx — mainChoices).
        return (
         <Tabs2 tabs={[
                  { label: 'Tableau de bord', keepMounted: true, children: infoBlock },
                  { label: 'Ressources',      children: <AdminResourcesTab adminId={admin.id} email={admin.email} /> },
                  { label: 'Événements',      children: <AdminEventsTab creatorId={admin.id} creatorName={admin.pseudonym || `${admin.firstname} ${admin.lastname}`.trim() || admin.email} /> },
                  { label: 'Achats',          children: <PurchaseManagment     email={admin.email} creationDate={admin.creationDate} /> },
                  { label: 'Abonnements',     children: <SubscriptionManagment email={admin.email} /> },
                  { label: 'Emails',          children: <SentEmailManagment    email={admin.email} /> },
                  { label: 'Logs',            children: <AdminActionLogManagment adminId={admin.id} /> },
                ]} />
        );

       })()
      }

    </div>
  );
};

export default AdminSheet;
